import {Controller} from '@hotwired/stimulus'
import TomSelect from 'tom-select'
import 'tom-select/dist/css/tom-select.bootstrap4.css'

export default class extends Controller {
  static values = {
    updateUrl: String,
    initialTags: Array,
    availableTags: Array,
    disabled: Boolean,
    taggableType: String,
    taggableId: Number,
    poolId: String,
  }

  connect() {
    const tags = this.initialTagsValue || []
    const available = this.getAvailableTags()

    if (this.disabledValue) {
      this.element.innerHTML = ''
      tags.forEach((name) => {
        const span = document.createElement('span')
        span.className = 'item badge tag'
        span.textContent = name
        this.element.appendChild(span)
      })
      return
    }

    const select = document.createElement('select')
    select.multiple = true

    const allOptions = [...new Set([...tags, ...available])]
    allOptions.forEach((name) => {
      const opt = document.createElement('option')
      opt.value = name
      opt.textContent = name
      opt.selected = tags.includes(name)
      select.appendChild(opt)
    })

    this.element.innerHTML = ''
    this.element.appendChild(select)

    this.tomSelect = new TomSelect(select, {
      itemClass: 'item badge tag',
      plugins: ['remove_button'],
      openOnFocus: true,
      createOnBlur: true,
      create: (input) => {
        const name = input.toLowerCase()
        this.addTagToPool(name)
        return {value: name, text: name}
      },
      sortField: {field: 'text', direction: 'asc'},
      disabledField: 'disabled',
      hidePlaceholder: true, // hide placeholder when no tags are selected
      onChange: (values) => this.saveTagNames(values),
      onDropdownOpen: () => {
        let added = false
        this.getAvailableTags().forEach((name) => {
          if (!this.tomSelect.options[name]) {
            this.tomSelect.addOption({value: name, text: name})
            added = true
          }
        })
        if (added)
          this.tomSelect.refreshOptions(false)
      },
      render: {
        option_create: (data, escape) => {
          const escapedInput = escape(data.input)
          const addText = I18n.t('tags.editable_tags_selector.add_tag_name', {
            tag_name: `<strong>${escapedInput}</strong>`,
          })

          return `<div class="create">${addText}&hellip;</div>`
        },
        no_results(data, escape) {
          const escapedInput = escape(data.input)
          const noResultsText = I18n.t(
            'tags.editable_tags_selector.no_results_found',
            {
              tag_name: `<strong>${escapedInput}</strong>`,
            },
          )
          return `<div class="no-results">${noResultsText}</div>`
        },
      },
    })

    this.tomSelect.settings.placeholder = I18n.t('tags.add_tag')
  }

  disconnect() {
    if (this.tomSelect) {
      this.tomSelect.destroy()
      this.tomSelect = null
    }
  }

  getAvailableTags() {
    const poolEl = this.getPoolElement()
    if (poolEl) {
      try {
        return JSON.parse(poolEl.dataset.tagPoolAvailableTags || '[]')
      } catch {
        return []
      }
    }
    return this.availableTagsValue || []
  }

  addTagToPool(name) {
    const poolEl = this.getPoolElement()
    if (!poolEl)
      return
    try {
      const current = JSON.parse(poolEl.dataset.tagPoolAvailableTags || '[]')
      if (!current.includes(name))
        poolEl.dataset.tagPoolAvailableTags = JSON.stringify([...current, name])
    } catch {
      // ignore parse errors
    }
  }

  getPoolElement() {
    return this.poolIdValue ? document.getElementById(this.poolIdValue) : null
  }

  async saveTagNames(tagNames) {
    if (!this.updateUrlValue || !this.taggableTypeValue || !this.taggableIdValue)
      return

    const token = document.querySelector('meta[name="csrf-token"]')?.content
    try {
      const res = await fetch(this.updateUrlValue, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': token,
          Accept: 'text/vnd.turbo-stream.html, application/json',
        },
        body: JSON.stringify({
          taggable_type: this.taggableTypeValue,
          taggable_id: this.taggableIdValue,
          tags: {names: tagNames},
        }),
      })

      if (!res.ok) {
        // eslint-disable-next-line no-console
        console.error('editable-tags: save failed', res.statusText)
        this.showSaveError()
        return
      }

      const text = await res.text()
      if (text && window.Turbo?.renderStreamMessage)
        window.Turbo.renderStreamMessage(text)
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('editable-tags: save error', err)
      this.showSaveError()
    }
  }

  showSaveError() {
    this.element.querySelector('.editable-tags-error')?.remove()

    const error = document.createElement('div')
    error.className = 'editable-tags-error alert alert-danger mt-1 py-1 px-2 small'
    error.setAttribute('role', 'alert')
    error.textContent = I18n.t('tags.save_error')
    this.element.insertAdjacentElement('afterend', error)

    setTimeout(() => error.remove(), 5000)
  }
}
